import { useMemo, useState } from 'react';
import {
  coverageState,
  formatAddress,
  OUTCOME_LABELS,
  type CoverageState,
} from '@yucanvas/shared';
import { useAuth } from '../auth/AuthProvider';
import { useTurfData } from '../data/TurfDataProvider';
import { COVERAGE_COLORS, COVERAGE_LABELS } from '../map/colors';
import { VisitSheet } from '../components/VisitSheet';

/**
 * Street-by-street walking view. Sorted by street then house number, because
 * that is the order a canvasser physically walks -- alphabetical on the whole
 * address string puts 1000 Main before 99 Main.
 */
function sortKey(address: string): [string, number] {
  const match = /^(\d+)\s+(.*)$/.exec(address.trim());
  if (!match) return [address.toLowerCase(), 0];
  return [(match[2] ?? '').toLowerCase(), Number(match[1])];
}

const DONE: CoverageState[] = ['contacted', 'skip', 'do_not_contact'];

export function ListScreen() {
  const { profile } = useAuth();
  const { rows, loading, readOnly, peopleAt, notesAt, visitsAt } = useTurfData();
  const [hideDone, setHideDone] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const withState = rows.map((h) => ({ household: h, state: coverageState(h) }));
    const visible = hideDone
      ? withState.filter((r) => !DONE.includes(r.state))
      : withState;
    return visible.sort((a, b) => {
      const [streetA, numA] = sortKey(a.household.street_address);
      const [streetB, numB] = sortKey(b.household.street_address);
      if (streetA !== streetB) return streetA.localeCompare(streetB);
      if (numA !== numB) return numA - numB;
      return (a.household.unit ?? '').localeCompare(b.household.unit ?? '');
    });
  }, [rows, hideDone]);

  const selected = rows.find((h) => h.id === selectedId) ?? null;

  return (
    <div className="list-wrap">
      <div className="list__bar">
        <span className="muted">
          {loading ? 'Loading…' : `${sorted.length} addresses`}
        </span>
        <label className="toggle toggle--inline">
          <input
            type="checkbox"
            checked={hideDone}
            onChange={(e) => setHideDone(e.target.checked)}
          />
          <span>Hide finished</span>
        </label>
      </div>

      <ul className="list">
        {sorted.map(({ household, state }) => (
          <li key={household.id}>
            <button className="row" onClick={() => setSelectedId(household.id)}>
              <i className="row__dot" style={{ background: COVERAGE_COLORS[state] }} />
              <span className="row__addr">{formatAddress(household)}</span>
              <span className="row__state">
                {household.last_visit
                  ? OUTCOME_LABELS[household.last_visit.outcome]
                  : COVERAGE_LABELS[state]}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {sorted.length === 0 && !loading ? (
        <p className="empty">Nothing left in this turf.</p>
      ) : null}

      {selected && profile ? (
        <VisitSheet
          household={selected}
          people={peopleAt(selected.id)}
          notes={notesAt(selected.id)}
          visits={visitsAt(selected.id)}
          readOnly={readOnly}
          canvasserId={profile.id}
          canvasserName={profile.name}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </div>
  );
}
